<script setup>
  import HomeButton from '@/components/home/HomeButton.vue';
  import HomeSectionHeading from '@/components/home/HomeSectionHeading.vue';

  import { HOME_SEE_PRICE } from '@/constants/home';

  import markerArrow from '@/assets/images/home/marker-arrow.svg';
  import housePhoto from '@/assets/images/home/see-price-house.webp';

  defineEmits(['get-price']);

  // Marker centres in px of the 520px design photo
  const MARKERS = [
    { x: 441, y: 181, withPauses: false },
    { x: 140, y: 392, withPauses: false },
    { x: 180, y: 127, withPauses: true },
  ];
</script>

<template>
  <section id="see-price" class="w-full scroll-mt-27 md:scroll-mt-30 lg:scroll-mt-34">
    <div
      class="mx-auto flex w-full max-w-299 flex-col items-center px-5 pb-19 md:px-8 md:pb-28 lg:flex-row lg:items-start lg:justify-between xl:px-0 xl:pb-35"
    >
      <div
        v-reveal
        class="photo relative hidden w-full max-w-130 md:mb-12 md:block lg:mt-17 lg:mb-0 lg:w-[43.5%] lg:max-w-none"
      >
        <img
          :src="housePhoto"
          :alt="HOME_SEE_PRICE.imageAlt"
          width="1040"
          height="1042"
          loading="lazy"
          class="block h-auto w-full"
        />
        <span
          v-for="marker in MARKERS"
          :key="`${marker.x}-${marker.y}`"
          :class="['marker', marker.withPauses && 'marker-with-pauses']"
          :style="{
            left: `calc(var(--design-px) * ${marker.x})`,
            top: `calc(var(--design-px) * ${marker.y})`,
          }"
          aria-hidden="true"
        >
          <img :src="markerArrow" alt="" class="w-[48%]" />
        </span>
      </div>

      <div
        v-reveal="150"
        class="flex flex-col items-center text-center lg:w-[47%] lg:items-start lg:text-left"
      >
        <HomeSectionHeading
          align-start-on-desktop
          :eyebrow="HOME_SEE_PRICE.eyebrow"
          :title="HOME_SEE_PRICE.title"
        />

        <p
          class="text-ink mt-5 max-w-85 text-[13px]/4.5 font-medium sm:max-w-120 sm:text-[15px]/5.5 md:max-w-150 md:text-[16px]/6 lg:mt-7 lg:max-w-130.5 lg:text-[18px]/6.5"
        >
          {{ HOME_SEE_PRICE.description }}
        </p>

        <ul
          class="text-ink mt-5 flex flex-col self-stretch text-left text-[20px]/11 font-semibold sm:self-center md:text-[22px] lg:mt-10 lg:ml-1 lg:self-start xl:text-[27px]/11.5"
        >
          <li
            v-for="(benefit, index) in HOME_SEE_PRICE.benefits"
            :key="benefit"
            class="reveal-item flex gap-[0.5em] before:content-['•']"
            :style="{ '--item-delay': `calc(var(--reveal-delay, 0ms) + ${400 + index * 100}ms)` }"
          >
            {{ benefit }}
          </li>
        </ul>

        <HomeButton class="mt-10 lg:mt-12" @click="$emit('get-price')">
          {{ HOME_SEE_PRICE.cta }}
        </HomeButton>
      </div>
    </div>
  </section>
</template>

<style scoped>
  /* list items follow their v-reveal parent in one by one */
  .reveal:not(.is-revealed) .reveal-item {
    opacity: 0;
  }

  .is-revealed .reveal-item {
    animation: reveal-item-in 0.4s ease-out var(--item-delay) both;
  }

  @keyframes reveal-item-in {
    from {
      opacity: 0;
      transform: translateX(-12px);
    }
  }

  .photo {
    container-type: inline-size;
    --design-px: calc(100cqw / 520);
  }

  .marker {
    position: absolute;
    display: flex;
    width: calc(var(--design-px) * 40);
    height: calc(var(--design-px) * 40);
    align-items: center;
    justify-content: center;
    border: 3px solid var(--color-bright-cyan);
    border-radius: 9999px;
    background: #fff;
    translate: -50% -50%;
    animation: marker-pulse 2s ease-in-out infinite alternate;
  }

  .marker-with-pauses {
    animation: marker-pulse-with-pauses 5s ease-in-out infinite;
  }

  @keyframes marker-pulse {
    from {
      width: calc(var(--design-px) * 40);
      height: calc(var(--design-px) * 40);
    }

    to {
      width: calc(var(--design-px) * 68);
      height: calc(var(--design-px) * 68);
    }
  }

  /* with 0.5 s pauses at both ends */
  @keyframes marker-pulse-with-pauses {
    0%,
    10%,
    100% {
      width: calc(var(--design-px) * 40);
      height: calc(var(--design-px) * 40);
    }

    50%,
    60% {
      width: calc(var(--design-px) * 68);
      height: calc(var(--design-px) * 68);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .marker,
    .marker-with-pauses {
      animation: none;
    }
  }
</style>
